import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarGrid, RangeCalendarGrid } from "@majourneys/booking/pickers";
import { Icon } from "@majourneys/icons";
import { MAX_TRIP_PEOPLE } from "../../state/trip-storage";
import { DEFAULT_CHILDREN_MAX_AGE } from "@majourneys/shared";
import { MAX_CHILD_AGE, MAX_TRIP_CHILDREN, type TripParty } from "../../lib/trip-party";
import { formatNumber } from "../../internal/format";

/** The longest trip the create sheet makes, in days (the day stepper's ceiling). */
export const MAX_TRIP_DAYS = 14;

function utc(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

/** Days from `start` to `end` inclusive. One when there is no end. */
export function daysInRange(start: string, end: string | null): number {
  if (!end) return 1;
  return Math.max(1, Math.round((utc(end).getTime() - utc(start).getTime()) / 86_400_000) + 1);
}

/** `start` plus `days - 1`, as YYYY-MM-DD: the last day of a trip. */
export function rangeEnd(start: string, days: number): string {
  const d = utc(start);
  d.setUTCDate(d.getUTCDate() + Math.max(0, days - 1));
  return d.toISOString().slice(0, 10);
}

function useShortDate() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) || "en";
  return useMemo(() => {
    let fmt: Intl.DateTimeFormat;
    try {
      fmt = new Intl.DateTimeFormat(`${lang}-u-nu-latn`, {
        weekday: "short",
        day: "numeric",
        month: "short",
        timeZone: "UTC",
      });
    } catch {
      fmt = new Intl.DateTimeFormat("en", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
    }
    return (iso: string) => fmt.format(utc(iso));
  }, [lang]);
}

/**
 * "When": a chip that reads the chosen dates and opens the pewter range
 * calendar under it. Never a native date input.
 *
 * Tap a first day, then a last day; a single tap is a one-day trip. The range
 * is what sets the trip's day count, so a trip longer than the planner allows
 * is cut to its first fourteen days rather than refused.
 */
export function TripDatesField({
  start,
  end,
  onChange,
}: {
  start: string | null;
  end: string | null;
  onChange: (next: { start: string | null; end: string | null }) => void;
}) {
  const { t } = useTranslation();
  const short = useShortDate();
  const [open, setOpen] = useState(false);
  const labelId = useId();
  const panelId = useId();
  const days = start ? daysInRange(start, end) : 0;

  return (
    <>
      <div className="mj-trip-dialog__field">
        <span id={labelId}>{t("trip.when.label")}</span>
        <button
          type="button"
          className={`mj-trip-when__chip${start ? " is-set" : ""}`}
          aria-expanded={open}
          aria-controls={panelId}
          aria-labelledby={labelId}
          aria-describedby={`${labelId}-value`}
          onClick={() => setOpen((o) => !o)}
        >
          <Icon name="ri-calendar-line" aria-hidden="true" />
          <b id={`${labelId}-value`}>
            {start
              ? `${end && end !== start ? t("trip.when.range", { start: short(start), end: short(end) }) : short(start)} · ${t("trip.day_count", { count: days }).replace(/ /g, "\u00A0")}`
              : t("trip.when.pick")}
          </b>
          <Icon
            name="ri-arrow-down-s-line"
            className={`mj-trip-when__caret${open ? " is-open" : ""}`}
            aria-hidden="true"
          />
        </button>
      </div>
      {open && (
        <div id={panelId} className="mj-trip-when__calendar mj-fade-in">
          <RangeCalendarGrid
            checkIn={start}
            checkOut={end}
            minNights={1}
            onChange={(next) => {
              const s = next.checkIn;
              let e = next.checkOut;
              if (s && e && daysInRange(s, e) > MAX_TRIP_DAYS) e = rangeEnd(s, MAX_TRIP_DAYS);
              onChange({ start: s, end: e });
              // A full range is a made choice: fold the calendar back into
              // its chip, which reopens it on a tap.
              if (s && e) setOpen(false);
            }}
          />
        </div>
      )}
    </>
  );
}

/** "Day 1": one date, for a trip that already has its days. */
export function TripStartField({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (iso: string) => void;
}) {
  const { t } = useTranslation();
  const short = useShortDate();
  const labelId = useId();
  return (
    <>
      <div className="mj-trip-dialog__field">
        <span id={labelId}>{t("trip.when.first_day")}</span>
        <b className="mj-trip-when__value" aria-live="polite">
          {value ? short(value) : t("trip.when.pick")}
        </b>
      </div>
      <div className="mj-trip-when__calendar" role="group" aria-labelledby={labelId}>
        <CalendarGrid selected={value} onSelect={onChange} startAtSelected />
      </div>
    </>
  );
}

/** "Who": a whole number of people, one to the cap, on a stepper. */
export function TripPeopleStepper({
  value,
  onChange,
}: {
  value: number;
  onChange: (next: number) => void;
}) {
  const { t, i18n } = useTranslation();
  const labelId = useId();
  return (
    <div className="mj-trip-dialog__field mj-trip-when__people">
      <span id={labelId}>{t("trip.when.who")}</span>
      <div className="mj-trip-dialog__days" role="group" aria-labelledby={labelId}>
        <button
          type="button"
          onClick={() => onChange(Math.max(1, value - 1))}
          disabled={value <= 1}
          aria-label={t("trip.when.fewer_people")}
        >
          <Icon name="ri-subtract-line" aria-hidden="true" />
        </button>
        <strong aria-live="polite" aria-label={t("trip.when.people", { count: value })}>
          {formatNumber(value, i18n.language)}
        </strong>
        <button
          type="button"
          onClick={() => onChange(Math.min(MAX_TRIP_PEOPLE, value + 1))}
          disabled={value >= MAX_TRIP_PEOPLE}
          aria-label={t("trip.when.more_people")}
        >
          <Icon name="ri-add-line" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/** The age a newly added child starts at, before the guest sets it. */
const NEW_CHILD_AGE = 8;

/** One row of a stepper: a label, an optional hint, minus, value, plus. */
function StepRow({
  label,
  hint,
  value,
  shown,
  min,
  max,
  lessLabel,
  moreLabel,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number;
  shown: string;
  min: number;
  max: number;
  lessLabel: string;
  moreLabel: string;
  onChange: (next: number) => void;
}) {
  const labelId = useId();
  return (
    <div className="mj-trip-dialog__field mj-trip-when__people">
      <span id={labelId}>
        {label}
        {hint ? <small className="mj-trip-when__hint">{hint}</small> : null}
      </span>
      <div className="mj-trip-dialog__days" role="group" aria-labelledby={labelId}>
        <button type="button" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} aria-label={lessLabel}>
          <Icon name="ri-subtract-line" aria-hidden="true" />
        </button>
        <strong aria-live="polite">{shown}</strong>
        <button type="button" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} aria-label={moreLabel}>
          <Icon name="ri-add-line" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/**
 * "Who": adults, children, and each child's age. Ages set the child price and
 * the age limits activity by activity (`splitParty`), so a family of four is
 * quoted as the booking sheet would quote it. Steppers only: never the
 * browser's own select (owner rule).
 */
export function TripPartyFields({
  value,
  onChange,
}: {
  value: TripParty;
  onChange: (next: TripParty) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const kids = value.childAges.length;
  const maxAdults = MAX_TRIP_PEOPLE - kids;
  const maxKids = Math.min(MAX_TRIP_CHILDREN, MAX_TRIP_PEOPLE - value.adults);
  const setKids = (n: number) => {
    const ages = value.childAges.slice(0, n);
    while (ages.length < n) ages.push(NEW_CHILD_AGE);
    onChange({ ...value, childAges: ages });
  };
  const setAge = (i: number, age: number) =>
    onChange({ ...value, childAges: value.childAges.map((a, j) => (j === i ? age : a)) });
  return (
    <div className="mj-trip-when__party" role="group" aria-label={t("trip.when.who")}>
      <StepRow
        label={t("trip.when.adults")}
        hint={t("trip.when.adults_hint", { min: DEFAULT_CHILDREN_MAX_AGE + 1 })}
        value={value.adults}
        shown={formatNumber(value.adults, lang)}
        min={1}
        max={maxAdults}
        lessLabel={t("trip.when.fewer_adults")}
        moreLabel={t("trip.when.more_adults")}
        onChange={(adults) => onChange({ ...value, adults })}
      />
      <StepRow
        label={t("trip.when.children")}
        hint={t("trip.when.children_hint")}
        value={kids}
        shown={formatNumber(kids, lang)}
        min={0}
        max={maxKids}
        lessLabel={t("trip.when.fewer_children")}
        moreLabel={t("trip.when.more_children")}
        onChange={setKids}
      />
      {value.childAges.map((age, i) => (
        <StepRow
          key={i}
          label={t("trip.when.child_age", { n: formatNumber(i + 1, lang) })}
          value={age}
          shown={t("trip.when.age", { count: age, defaultValue: String(age) })}
          min={0}
          max={MAX_CHILD_AGE}
          lessLabel={t("trip.when.younger", { n: formatNumber(i + 1, lang) })}
          moreLabel={t("trip.when.older", { n: formatNumber(i + 1, lang) })}
          onChange={(a) => setAge(i, a)}
        />
      ))}
      {kids > 0 ? <p className="mj-trip-when__note">{t("trip.when.ages_note")}</p> : null}
    </div>
  );
}
